import { useEffect, useRef, useCallback, useState } from 'react';
import { useRecoilState } from 'recoil';
import { DownloadIcon } from '@img/icons/DownloadIcon/DownloadIcon';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { BulkDownloadState } from '@state';
import { useInitiateBulkDownload } from '@api';
import { useGetShipperTokenHeader, useLongPoll } from '@hooks';
import { Button, Text, Caption, ModuleToast } from '@components';
import { BUTTON_SIZE, BUTTON_TYPES, TEXT_WEIGHT, TBulkDocType, TBulkDownloadState } from '@types';
import styles from './DownloadToast.module.scss';

// The download icon defaults to navy (its button appearance); the bulk-download
// toast deliberately uses the functional blue as a status accent.
const FunctionalDownloadIcon = () => <DownloadIcon color={'var(--func-blue-default)'} />;

const DOC_TYPE_LABELS: Record<TBulkDocType, string> = {
	POD: 'PODs',
	PALLET_LABEL: 'Pallet Labels',
	CONSIGNMENT_NOTE: 'Consignment Notes',
};

const POLL_INTERVAL_MS = 1000;
const AUTO_DISMISS_MS = 60000;
const MAX_VISIBLE = 3;
// Stop polling after ~5 minutes so a stuck job never hammers the endpoint forever
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

const STATUS_ENDPOINT = (label: string) => `/api/shipper/bulk-document-download/${label}`;

interface DownloadToastEntryProps {
	initialState: TBulkDownloadState;
	onRemove: () => void;
}

const DownloadToastEntry = (props: DownloadToastEntryProps) => {
	const { initialState, onRemove } = props;
	const [state, setState] = useState(initialState);
	const { label, docType, shipmentIds, labelSize, processed, total, downloadUrl, status } = state;

	const { callInitiateBulkDownload } = useInitiateBulkDownload();
	const shipperTokenHeader = useGetShipperTokenHeader();
	const callInitiateRef = useRef(callInitiateBulkDownload);
	callInitiateRef.current = callInitiateBulkDownload;
	const shipperTokenHeaderRef = useRef(shipperTokenHeader);
	shipperTokenHeaderRef.current = shipperTokenHeader;

	const {
		pollData,
		pollError,
		clearPollData,
		clearPollError,
		startPolling,
		cancelPolling,
	} = useLongPoll({ url: '', interval: POLL_INTERVAL_MS, method: 'GET' });

	// Caps a stuck job so it never polls the endpoint forever
	const pollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

	const docLabel = docType ? DOC_TYPE_LABELS[docType] : 'Documents';
	const fileLabel = `${total > 0 ? `${total} Shipment ` : ''}${docLabel} Bulk Download`;

	const stopPolling = useCallback(() => {
		cancelPolling();
		clearTimeout(pollTimeoutRef.current);
	}, [cancelPolling]);

	const handleRetry = useCallback(async () => {
		if (!shipmentIds.length || !docType) {
			setState(prev => ({ ...prev, status: 'FAILED' }));
			return;
		}
		// Clear the failed label first so polling only resumes once the new job label is returned
		setState(prev => ({ ...prev, label: null, status: 'IN_PROGRESS', processed: 0, skipped: 0, downloadUrl: null }));
		try {
			const result = await callInitiateRef.current({
				payload: { shipment_ids: shipmentIds, doc_type: docType, label_size: labelSize ?? null },
				headers: shipperTokenHeaderRef.current,
			});
			if (result?.data?.label) {
				setState(prev => ({ ...prev, label: result.data!.label, status: 'IN_PROGRESS' }));
			} else {
				setState(prev => ({ ...prev, status: 'FAILED' }));
			}
		} catch {
			setState(prev => ({ ...prev, status: 'FAILED' }));
		}
	}, [shipmentIds, docType, labelSize]);

	const handleOpenFolder = useCallback(() => {
		if (downloadUrl) {
			window.open(downloadUrl, '_blank', 'noopener,noreferrer');
		}
		onRemove();
	}, [downloadUrl, onRemove]);

	useEffect(() => {
		if (label && status === 'IN_PROGRESS') {
			startPolling({ overrideUrl: STATUS_ENDPOINT(label), headers: shipperTokenHeaderRef.current });
			clearTimeout(pollTimeoutRef.current);
			pollTimeoutRef.current = setTimeout(() => {
				stopPolling();
				setState(prev => ({ ...prev, status: 'FAILED' }));
			}, MAX_POLL_DURATION_MS);
		} else {
			stopPolling();
		}
		return () => stopPolling();
	}, [label, status]);

	useEffect(() => {
		if (!pollData) return;
		const { job_status, processed: p, total: t, skipped: sk, url } = pollData;
		if (job_status === 'DONE') {
			stopPolling();
			clearPollData();
			setState(prev => ({
				...prev,
				status: 'DONE',
				downloadUrl: url ?? null,
				processed: t ?? prev.total,
				total: t ?? prev.total,
				skipped: sk ?? 0,
			}));
		} else if (job_status === 'IN_PROGRESS' || job_status === 'NOT_STARTED') {
			setState(prev => ({
				...prev,
				processed: p ?? prev.processed,
				total: t ?? prev.total,
				skipped: sk ?? 0,
			}));
		} else {
			// FAILED or any unrecognised job_status — surface failure rather than polling forever
			stopPolling();
			clearPollData();
			setState(prev => ({ ...prev, status: 'FAILED' }));
		}
	}, [pollData]);

	useEffect(() => {
		if (!pollError) return;
		stopPolling();
		clearPollError();
		setState(prev => ({ ...prev, status: 'FAILED' }));
	}, [pollError]);

	useEffect(() => {
		if (status !== 'DONE') return;
		const timer = setTimeout(onRemove, AUTO_DISMISS_MS);
		return () => clearTimeout(timer);
	}, [status]);

	const isActive = status === 'IN_PROGRESS';
	const isDone = status === 'DONE';
	const isFailed = status === 'FAILED';

	const progressPct = total > 0 ? Math.round((processed / total) * 100) : 0;

	return (
		<div className={styles.entry}>
			<div className={styles.fileIcon}>
				<Caption>ZIP</Caption>
			</div>
			<div className={styles.content}>
				<div className={styles.filename}>
					<Text weight={TEXT_WEIGHT.SEMI_BOLD}>{fileLabel}</Text>
				</div>
				<div className={styles.progressWrap}>
					<div className={styles.progressTrack} />
					<div
						className={`${styles.progressBar} ${isDone ? styles.complete : isFailed ? styles.failed : styles.active}`}
						style={isDone || isFailed ? undefined : { width: `${progressPct}%` }}
					/>
				</div>
				{isActive && (
					<div className={styles.statusRow}>
						<Caption>{total > 0 ? `${processed} of ${total} shipments` : 'Starting…'}</Caption>
						{total > 0 && (
							<Button
								text='Cancel'
								onClick={onRemove}
								type={BUTTON_TYPES.TERTIARY}
								size={BUTTON_SIZE.SMALL}
							/>
						)}
					</div>
				)}
				{isFailed && (
					<div className={styles.statusRow}>
						<Caption color='var(--func-red-dark)'>Failed to prepare download</Caption>
						<Button
							text='Retry'
							onClick={handleRetry}
							type={BUTTON_TYPES.TERTIARY}
							size={BUTTON_SIZE.SMALL}
						/>
					</div>
				)}
				{isDone && (
					<div className={styles.statusRow}>
						<Caption color='var(--func-green-dark)'>Download complete</Caption>
						<Button
							text='Open folder'
							onClick={handleOpenFolder}
							type={BUTTON_TYPES.TERTIARY}
							size={BUTTON_SIZE.SMALL}
						/>
					</div>
				)}
			</div>
			<Button
				iconOnly
				Icon={CloseIcon}
				text='Dismiss'
				onClick={onRemove}
				type={BUTTON_TYPES.TERTIARY}
				size={BUTTON_SIZE.SMALL}
			/>
		</div>
	);
};

export const DownloadToast = () => {
	const [items, setItems] = useRecoilState(BulkDownloadState);
	const [showAll, setShowAll] = useState(false);

	const handleRemove = useCallback((label: string) => {
		setItems(prev => prev.filter(item => item.label !== label));
	}, [setItems]);

	const handleCloseAll = useCallback(() => {
		setItems([]);
	}, [setItems]);

	if (items.length === 0) return null;

	// Active/Failed first, Done last
	const sorted = [...items].sort((a, b) => {
		const isDoneA = a.status === 'DONE' ? 1 : 0;
		const isDoneB = b.status === 'DONE' ? 1 : 0;
		return isDoneA - isDoneB;
	});

	const visibleItems = showAll ? sorted : sorted.slice(0, MAX_VISIBLE);
	const hiddenCount = sorted.length - MAX_VISIBLE;

	const activeCount = items.filter(i => i.status === 'IN_PROGRESS').length;
	const failedCount = items.filter(i => i.status === 'FAILED').length;
	const doneCount = items.filter(i => i.status === 'DONE').length;

	const summaryParts: string[] = [];
	if (activeCount > 0) summaryParts.push(`${activeCount} in progress`);
	if (failedCount > 0) summaryParts.push(`${failedCount} failed`);
	if (doneCount > 0) summaryParts.push(`${doneCount} completed`);
	const summaryText = summaryParts.join('  •  ');

	return (
		<ModuleToast
			title='Downloads'
			Icon={FunctionalDownloadIcon}
			onClose={handleCloseAll}
			collapsedSubtitle={summaryText}
		>
			<div className={styles.entries}>
				{visibleItems.map(item => (
					<DownloadToastEntry
						key={item.label}
						initialState={item}
						onRemove={() => handleRemove(item.label!)}
					/>
				))}
			</div>
			{!showAll && hiddenCount > 0 && (
				<div className={styles.showMore}>
					<Button
						text={`Show ${hiddenCount} more`}
						Icon={AddIcon}
						onClick={() => setShowAll(true)}
						type={BUTTON_TYPES.TERTIARY}
						size={BUTTON_SIZE.SMALL}
					/>
				</div>
			)}
			{showAll && sorted.length > MAX_VISIBLE && (
				<div className={styles.showMore}>
					<Button
						text='Show less'
						Icon={RemoveIcon}
						onClick={() => setShowAll(false)}
						type={BUTTON_TYPES.TERTIARY}
						size={BUTTON_SIZE.SMALL}
					/>
				</div>
			)}
		</ModuleToast>
	);
};
